import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import type {
  HhrgAvailable,
  HhrgDisciplineCostInput,
  HhrgDisciplineCostOutput,
  HhrgPeriodNumber,
  HhrgPeriodOutput,
  HhrgUnavailable,
  HhrgWorksheetOutput,
} from '../../utils/hhrgWorksheetCombination';

const VISITS_INPUT_PATTERN = /^\d+$/;

export interface HhrgWorksheetPresentationProps {
  output: HhrgWorksheetOutput;
  onCostsChange?: (
    periodNumber: HhrgPeriodNumber,
    costs: readonly HhrgDisciplineCostInput[]
  ) => void;
  onResetCosts?: (periodNumber: HhrgPeriodNumber) => void;
}

type HhrgResult = HhrgAvailable<number> | HhrgUnavailable<string>;

const UNAVAILABLE_REASON_LABELS: Record<string, string> = {
  lupa: 'No covered visits to price',
  pep: 'partial episode adjustment (PEP)',
  outlier: 'outlier calculation',
  late_noa: 'late notice of admission',
  missing_input: 'required payment input is missing',
  request_failed: 'revenue estimate could not be loaded',
  unsupported: 'unsupported calculation',
  missing_cost: 'cost is not configured',
  zero_revenue: 'revenue is zero',
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const formatCurrency = (value: number): string =>
  currencyFormatter.format(value);

const formatNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);

const formatResult = (
  result: HhrgResult,
  format: (value: number) => string
): string => {
  if (result.status === 'available') return format(result.value);
  return `Unavailable: ${UNAVAILABLE_REASON_LABELS[result.reason] ?? result.reason}`;
};

const displayText = (value?: string | number | null): string =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && !value.trim())
    ? '—'
    : String(value);

function PaymentGroupDetails({ period }: { period: HhrgPeriodOutput }) {
  const details = [
    ['OASIS source', period.display.oasisSource],
    ['OASIS date', period.display.oasisDate],
    ['Primary diagnosis', period.display.primaryDiagnosis],
    ['Admission source', period.display.admissionSource],
    ['Admission timing', period.display.admissionTiming],
    ['Clinical group', period.display.clinicalGroup],
    ['Clinical level', period.display.clinicalLevel],
    ['Comorbidity adjustment', period.display.comorbidityAdjustment],
    ['LUPA status', period.display.lupaStatus],
    ['HIPPS code', period.display.hippsCode],
    ['Payment status', period.display.paymentStatus],
    ['Covered visits', period.display.coveredVisitCount],
    ['LUPA threshold', period.display.lupaThreshold],
    ['Case-mix weight', period.display.caseMixWeight],
    ['National rate', period.display.nationalRate],
    ['Wage index', period.display.wageIndex],
    ['Wage area', period.display.wageIndexAreaCode],
    ['QRP status', period.display.qualityReportingStatus],
  ] as const;

  return (
    <section
      aria-labelledby={`hhrg-payment-group-${period.display.periodNumber}`}
    >
      <h3
        id={`hhrg-payment-group-${period.display.periodNumber}`}
        className='mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'
      >
        Payment group details
      </h3>
      <dl className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
        {details.map(([label, value]) => (
          <div key={label} className='min-w-0'>
            <dt className='text-xs text-muted-foreground'>{label}</dt>
            <dd className='break-words font-medium text-foreground'>
              {displayText(value)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CostRows({
  costs,
  periodNumber,
  onCostsChange,
  onResetCosts,
}: {
  costs: readonly HhrgDisciplineCostOutput[];
  periodNumber: HhrgPeriodNumber;
  onCostsChange?: HhrgWorksheetPresentationProps['onCostsChange'];
  onResetCosts?: HhrgWorksheetPresentationProps['onResetCosts'];
}) {
  const headingId = `hhrg-costs-heading-period-${periodNumber}`;
  const editable = onCostsChange !== undefined;
  const updateCost = (
    index: number,
    patch: Partial<HhrgDisciplineCostInput>
  ) => {
    if (!onCostsChange) return;
    onCostsChange(
      periodNumber,
      costs.map(({ discipline, estimatedVisits, perVisitCost }, costIndex) =>
        costIndex === index
          ? { discipline, estimatedVisits, perVisitCost, ...patch }
          : { discipline, estimatedVisits, perVisitCost }
      )
    );
  };

  return (
    <section aria-labelledby={headingId}>
      <h3
        id={headingId}
        className='mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'
      >
        Agency cost estimate
      </h3>
      {costs.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          Unavailable: visit count and cost are not configured
        </p>
      ) : (
        <Table className='min-w-[520px] text-sm'>
          <TableHeader>
            <TableRow>
              <TableHead className='whitespace-nowrap'>Service</TableHead>
              <TableHead className='whitespace-nowrap text-right'>
                Visit count
              </TableHead>
              <TableHead className='whitespace-nowrap text-right'>
                Per-visit cost
              </TableHead>
              <TableHead className='whitespace-nowrap text-right'>
                Total expense
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {costs.map((cost, index) => (
              <TableRow key={cost.discipline}>
                <TableCell className='font-medium'>{cost.discipline}</TableCell>
                <TableCell className='text-right'>
                  {editable ? (
                    <input
                      aria-label={`Visit count for ${cost.discipline} in period ${periodNumber}`}
                      className='w-24 rounded border px-2 py-1 text-right'
                      min='0'
                      step='1'
                      type='number'
                      value={cost.estimatedVisits}
                      onChange={event => {
                        const raw = event.currentTarget.value;
                        if (raw === '') {
                          // Keep this numeric field editable when a clinician
                          // clears it before entering the replacement count.
                          updateCost(index, { estimatedVisits: 0 });
                          return;
                        }
                        if (!VISITS_INPUT_PATTERN.test(raw)) return;
                        updateCost(index, { estimatedVisits: Number(raw) });
                      }}
                    />
                  ) : (
                    formatNumber(cost.estimatedVisits)
                  )}
                </TableCell>
                <TableCell className='text-right'>
                  {editable ? (
                    <input
                      aria-label={`Per-visit cost for ${cost.discipline} in period ${periodNumber}`}
                      className='w-28 rounded border px-2 py-1 text-right'
                      min='0'
                      step='0.01'
                      type='number'
                      value={cost.perVisitCost ?? ''}
                      onChange={event => {
                        const raw = event.currentTarget.value;
                        if (raw === '') {
                          updateCost(index, { perVisitCost: null });
                          return;
                        }
                        const value = Number(raw);
                        if (Number.isFinite(value) && value >= 0) {
                          updateCost(index, { perVisitCost: value });
                        }
                      }}
                    />
                  ) : cost.perVisitCost === null ? (
                    'Unavailable: cost is not configured'
                  ) : (
                    formatCurrency(cost.perVisitCost)
                  )}
                </TableCell>
                <TableCell className='text-right'>
                  {formatResult(cost.totalExpense, formatCurrency)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {editable && onResetCosts ? (
        <Button
          type='button'
          size='sm'
          variant='outline'
          onClick={() => onResetCosts(periodNumber)}
        >
          Reset period {periodNumber} to loaded values
        </Button>
      ) : null}
    </section>
  );
}

function PeriodCard({
  period,
  onCostsChange,
  onResetCosts,
}: {
  period: HhrgPeriodOutput;
  onCostsChange?: HhrgWorksheetPresentationProps['onCostsChange'];
  onResetCosts?: HhrgWorksheetPresentationProps['onResetCosts'];
}) {
  const periodTitle = `Period ${period.display.periodNumber}`;
  const dateRange = `${period.display.startDate} – ${period.display.endDate}`;

  return (
    <Card
      data-testid={`hhrg-period-${period.display.periodNumber}`}
      className='min-w-0'
    >
      <CardHeader className='p-0'>
        <CardTitle className='text-lg'>{periodTitle}</CardTitle>
        <CardDescription>{dateRange}</CardDescription>
      </CardHeader>
      <CardContent className='space-y-5'>
        <PaymentGroupDetails period={period} />

        <div className='rounded-md border border-border/60 bg-muted/20 p-3'>
          <div className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
            {period.display.isEstimate ? 'Estimate' : 'OASIS revenue'}
          </div>
          <div className='mt-1 text-lg font-semibold text-foreground'>
            {formatResult(period.revenue, formatCurrency)}
          </div>
          {period.display.isEstimate && (
            <p className='mt-1 text-xs text-muted-foreground'>
              Projected from scheduled visits and the current OASIS. Not a Roger
              claim and not counted in AR.
            </p>
          )}
        </div>

        <CostRows
          costs={period.costs}
          periodNumber={period.display.periodNumber}
          onCostsChange={onCostsChange}
          onResetCosts={onResetCosts}
        />

        <dl className='grid grid-cols-1 gap-3 border-t pt-4 sm:grid-cols-3'>
          <div>
            <dt className='text-xs text-muted-foreground'>Total expense</dt>
            <dd className='font-semibold text-foreground'>
              {formatResult(period.totalExpense, formatCurrency)}
            </dd>
          </div>
          <div>
            <dt className='text-xs text-muted-foreground'>Profit / loss</dt>
            <dd className='font-semibold text-foreground'>
              {formatResult(period.profit, formatCurrency)}
            </dd>
          </div>
          <div>
            <dt className='text-xs text-muted-foreground'>Margin</dt>
            <dd className='font-semibold text-foreground'>
              {formatResult(period.margin, value => `${formatNumber(value)}%`)}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

export function HhrgWorksheetPresentation({
  output,
  onCostsChange,
  onResetCosts,
}: HhrgWorksheetPresentationProps) {
  return (
    <section
      data-testid='hhrg-worksheet-presentation'
      aria-labelledby='hhrg-worksheet-title'
      className='space-y-3'
    >
      <div>
        <h1 id='hhrg-worksheet-title' className='text-xl font-semibold'>
          HHRG worksheet
        </h1>
        <p className='text-sm text-muted-foreground'>
          Payment group, agency cost, and projected margin by billing period.
        </p>
      </div>
      <div className='overflow-x-auto'>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          {output.periods.map(period => (
            <PeriodCard
              key={period.display.periodNumber}
              period={period}
              onCostsChange={onCostsChange}
              onResetCosts={onResetCosts}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default HhrgWorksheetPresentation;
